import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getCaptcha, login, LoginRequest } from '../../services/auth';
import './login.css';

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // 加载验证码
  const loadCaptcha = async () => {
    try {
      const res = await getCaptcha();
      const data = res.data || res;
      setCaptchaId(data.captchaId);
      setCaptchaImage(data.image);
    } catch (error: any) {
      message.error(error.message || '加载验证码失败');
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  // 表单校验失败 - 将错误统一展示到顶部 Alert
  const handleFinishFailed = (errorInfo: any) => {
    const firstField = errorInfo.errorFields[0];
    setErrorMsg(firstField?.errors?.[0] || '请检查表单输入');
    // 清除字段级别的错误提示，避免重复显示
    form.setFields(
      errorInfo.errorFields.map((field: any) => ({
        name: field.name,
        errors: [],
      }))
    );
  };

  // 处理登录
  const handleLogin = async (values: LoginRequest) => {
    setLoading(true);
    try {
      const res = await login({
        ...values,
        captchaId: captchaId,
      });

      const data = res.data || res;

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      message.success('登录成功');
      navigate('/');
    } catch (error: any) {
      setErrorMsg(error.message || '登录失败');
      loadCaptcha();
      form.setFieldsValue({ captcha: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card" bordered={false}>
        <div className="login-title-wrapper">
          <div className="login-logo">
            <ThunderboltOutlined />
          </div>
          <div className="login-title">地震预警管理系统</div>
          <div className="login-subtitle">Earthquake Early Warning Management System</div>
        </div>

        <Form
          form={form}
          name="login"
          onFinish={handleLogin}
          onFinishFailed={handleFinishFailed}
          onFieldsChange={() => errorMsg && setErrorMsg('')}
          autoComplete="off"
          size="large"
        >
          {/* 错误提示 - 预留固定高度区域，防止布局跳动 */}
          <div style={{ minHeight: 42, marginBottom: 16 }}>
            {errorMsg && (
              <Alert
                message={errorMsg}
                type="error"
                showIcon
                closable
                onClose={() => setErrorMsg('')}
              />
            )}
          </div>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
              placeholder="手机号"
              maxLength={11}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              placeholder="密码"
            />
          </Form.Item>

          <Form.Item>
            <div className="captcha-wrapper">
              <Form.Item
                name="captcha"
                noStyle
                rules={[{ required: true, message: '请输入验证码' }]}
              >
                <Input
                  prefix={<SafetyOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="验证码"
                  maxLength={6}
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <img
                src={captchaImage}
                alt="验证码"
                className="captcha-image"
                onClick={loadCaptcha}
                title="点击刷新验证码"
              />
            </div>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              className="login-button"
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>

          <div className="login-tips">
            <p>使用手机号和密码登录系统</p>
            <p>初次登录请联系系统管理员创建账号</p>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
